/**
 * Entry point. Order matters (ARCHITECTURE.md §5 + ADR-011):
 *  1. TRAVEL-WINDOW BOOT CHECK — one IndexedDB read of the precomputed
 *     meta.travel_window (boot/travelWindow.ts). Inside a window, the Travel
 *     Card is the FIRST thing rendered (full-screen, wake-locked, zero
 *     network) and the app hydrates BEHIND it. Outside one — or on any
 *     failure — this adds a few ms of IDB open and falls through.
 *  2. mount React — the local-first paint happens inside App;
 *  3. register the service worker AFTER mount (never block the boot budget).
 * The error boundary wraps everything: no throw below it can blank the screen.
 */
import { StrictMode, useState } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app/App.tsx";
import { ErrorBoundary } from "./app/ErrorBoundary.tsx";
import { registerServiceWorker } from "./app/swRegistration.ts";
import { activeLegs, readTravelWindow } from "./boot/travelWindow.ts";
import type { TravelWindowLeg } from "./db/idb.ts";
import { TravelCard } from "./views/TravelCard.tsx";
import { shareKeyFromUrl } from "./sync/viewer.ts";
import { detectLocale, loadCatalog } from "./i18n.ts";
import "./styles.css";

const rootEl = document.getElementById("root");
if (rootEl === null) throw new Error("index.html is missing #root");
const root = createRoot(rootEl);

function Root({ travelLegs, gigName }: { travelLegs: TravelWindowLeg[]; gigName: string }) {
  const [showCard, setShowCard] = useState(travelLegs.length > 0);
  const locale = detectLocale();
  return (
    <>
      <App />
      {showCard && (
        <TravelCard locale={locale} legs={travelLegs} gigName={gigName} onDismiss={() => setShowCard(false)} />
      )}
    </>
  );
}

async function boot(): Promise<void> {
  // Hot path: number compares over a stored snapshot — no network, no auth,
  // no registry. readTravelWindow returns null on ANY failure (fall through).
  let legs: TravelWindowLeg[] = [];
  let gigName = "";
  // ADR-014: a share-key VIEWER session must never render the fast lane from
  // THIS device's persistent IndexedDB — that cache belongs to the owner/editor
  // who signed in here, and flashing it to a viewer on a shared phone would leak
  // another principal's itinerary. The viewer's own travel arrives scoped via
  // the /api/viewer path (App.tsx), never the owner's cache.
  // Load the active locale's catalog (a lazy chunk — the inactive locale no
  // longer ships in the shell) IN PARALLEL with the travel-window read, so the
  // fast lane never waits serially on i18n. Both feed the first paint; by
  // render time t() is ready. On repeat visits the chunk is SW-precached.
  const [meta] = await Promise.all([
    shareKeyFromUrl() !== null ? Promise.resolve<null>(null) : readTravelWindow(),
    loadCatalog(detectLocale()),
  ]);
  if (meta !== null) {
    legs = activeLegs(meta, Date.now());
    gigName = meta.gig_name;
  }

  root.render(
    <StrictMode>
      <ErrorBoundary locale={detectLocale()}>
        <Root travelLegs={legs} gigName={gigName} />
      </ErrorBoundary>
    </StrictMode>,
  );

  void registerServiceWorker();
}

void boot();
